/* eslint-disable no-undef */
import React, { useState } from "react";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import PropTypes from "prop-types";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { useDispatch } from "react-redux";
import { modalTypes } from "constants/types";
import { hideModal } from "slices/app/actions/modals";
import { ReactComponent as DictionaryIcon } from "icons/dictionary/dictionary.svg";
import { ReactComponent as FolderIcon } from "icons/folders.svg";
import { ReactComponent as PdfIcon } from "icons/pdf.svg";
import { ReactComponent as WordIcon } from "icons/word.svg";
import { ReactComponent as ArrowIcon } from "icons/dictionary/arrow.svg";
import { ReactComponent as ExcelIcon } from "icons/excel.svg";
import { displayName } from "utils/dictionary";

const Item = (type, name, folderId, documentId) => {
  const { workspaceId } = UserInfo;
  const [hover, setHover] = useState(false);

  const handleClick = () => {
    sessionStorage.setItem(
      "folderNavigation",
      `{"folderId": ${folderId}, "documentId": ${
        documentId || null
      }, "workspaceId": ${workspaceId}, "activeTab": "2"}`
    );
    window.location.href = "/#folders";
  };

  const handleKeyDown = ({ key }) => {
    if (key === "Enter") {
      handleClick();
    }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={
        type === "folder"
          ? "Click to navigate to this folder"
          : "Click to navigate to this document"
      }
      data-testid={type === "folder" ? `${type}-item` : "document-item"}
    >
      {hover ? <div className="highlight" /> : null}
      {type === "docx" || type === "doc" || type === "docm" ? (
        <WordIcon />
      ) : null}
      {type === "pdf" ? <PdfIcon /> : null}
      {type === "folder" ? <FolderIcon /> : null}
      {type === "xlsx" || type === "xls" || type === "xlsm" ? (
        <ExcelIcon />
      ) : null}
      <span className={`${hover ? "hover" : ""}`}>{displayName(name)}</span>
      {hover ? <ArrowIcon className="hover_arrow" /> : null}
    </div>
  );
};

const DetailsModal = ({ dictionaryName, folders, documents }) => {
  const dispatch = useDispatch();
  const hide = () => {
    dispatch(hideModal(modalTypes.DICTIONARY_FOLDER_DOC_INFO));
  };

  const foldersUsingDictionary = () => (
    <>
      <span data-testid="folders-using-dictionary">
        Folders Using This Dictionary ({folders.length})
      </span>
      {folders.map((item) => Item("folder", item.name, item.id))}
    </>
  );

  const documentsUsingDictionary = () => (
    <>
      <span data-testid="documents-using-dictionary">
        Documents Using This Dictionary ({documents.length})
      </span>
      {documents.map((item) =>
        Item(item.name.split(".").pop(), item.name, item.folderId, item.id)
      )}
    </>
  );

  const modalBody = () => (
    <div className="dict__uses__modal">
      <div>
        <p className="dict__uses__modal__title">
          <span>
            Here are the folders/documents currently using this dictionary
          </span>
        </p>
        <div className="dict__uses__modal__container">
          {folders.length > 0 && foldersUsingDictionary()}
          {documents.length > 0 && documentsUsingDictionary()}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      show
      onDismiss={hide}
      backdrop="static"
      centered
      className="fade-in dictionary_details_modal"
      data-testid="dictionary-uses-modal"
    >
      <Modal.Header closeButton>
        <div className="header">
          <DictionaryIcon />
          <b>{dictionaryName}</b>
        </div>
      </Modal.Header>
      <Modal.Body>{modalBody()}</Modal.Body>
      <Modal.Footer>
        <Button
          variant={buttonVariants.secondary}
          onClick={hide}
          content="Close"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default DetailsModal;

DetailsModal.propTypes = {
  dictionaryName: PropTypes.string.isRequired,
  folders: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  documents: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
};
